import { useEffect, useId, useState } from 'react';
import { Button } from '@heyroot/ui/primitives/Button';
import { Input } from '@heyroot/ui/primitives/Input';

export interface TypedConfirmProps {
  open: boolean;
  title: string;
  body: string;
  /** The exact string the operator must type, e.g. the target's email. */
  confirmValue: string;
  confirmLabel: string;
  requireReason: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
  pending?: boolean;
  /** A failure to tell the operator, announced to assistive technology when it appears. */
  error?: string | undefined;
  /** Keep Cancel disabled while `pending`, for an action that must not be abandoned half-way. Off by default. */
  lockCancelWhilePending?: boolean | undefined;
  /**
   * Fix round 2 (I5): the default text asserts the reason is "recorded in
   * the audit log" -- true for the tenant STATUS route, false for the
   * tenant-USER status route (its SetStatusRequest schema is
   * `z.object({ status })`; non-strict zod strips `reason`, and the admin
   * proxy writes no audit row for it). This is a SHARED component -- override
   * per call site rather than editing the default, which other callers rely
   * on being accurate.
   */
  reasonLabel?: string;
}

export function TypedConfirm(props: TypedConfirmProps) {
  const titleId = useId();
  const [typed, setTyped] = useState('');
  const [reason, setReason] = useState('');

  // Fix round 2 finding 5: this component is permanently mounted at its call
  // sites and only `return null`s when closed -- `typed`/`reason` otherwise
  // survive a cancel. Without this, typing the confirm value, cancelling, and
  // reopening leaves the confirm button already enabled with nothing
  // re-typed, defeating the gate on the one action it guards.
  useEffect(() => {
    if (!props.open) {
      setTyped('');
      setReason('');
    }
  }, [props.open]);

  if (!props.open) return null;

  const matches = typed === props.confirmValue;
  const reasonOk = !props.requireReason || reason.trim().length > 0;
  const canConfirm = matches && reasonOk && !props.pending;

  return (
    <div
      role="alertdialog"
      aria-labelledby={titleId}
      className="flex flex-col gap-3 rounded-control border border-danger bg-danger-pale p-4"
    >
      <h3 id={titleId} className="text-body-emphasis text-danger-text">
        {props.title}
      </h3>
      <p className="text-body-sm text-secondary">{props.body}</p>
      {props.error ? (
        <p role="alert" className="text-body-sm text-danger-text">
          {props.error}
        </p>
      ) : null}

      <label className="flex flex-col gap-1 text-body-sm text-secondary">
        Type {props.confirmValue} to confirm
        <Input
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          aria-label={`Type ${props.confirmValue} to confirm`}
          autoComplete="off"
        />
      </label>

      {props.requireReason ? (
        <label className="flex flex-col gap-1 text-body-sm text-secondary">
          {props.reasonLabel ?? 'Reason (recorded in the audit log)'}
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            aria-label={props.reasonLabel ?? 'Reason'}
          />
        </label>
      ) : null}

      <div className="flex gap-2">
        <Button
          variant="danger"
          disabled={!canConfirm}
          onClick={() => props.onConfirm(reason.trim())}
        >
          {props.confirmLabel}
        </Button>
        <Button
          variant="ghost"
          disabled={props.pending === true && props.lockCancelWhilePending === true}
          onClick={props.onCancel}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}
